/**
 * Copyright 2016 Taito United Oy
 * All rights reserved.
 */

import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import {
  FormattedMessage,
  FormattedHTMLMessage,
  injectIntl,
  intlShape,
} from 'react-intl';
import styled from 'styled-components';

// Components
import CircleWidget from './CircleWidget';
import Box, { Minifiable } from '../../components/Box';

// Messages
import messages from './messages/ReportDetails_messages';

const propTypes = {
  report: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
};

class ReportDetails extends PureComponent {
  constructor(props) {
    super(props);

    this.boxRef = React.createRef();
    this.state = {
      detailToShow: null,
    };
  }

  onStateChange = () => {
    /* The parent container's minified-state has changed.
     * Force re-render to change the detail layout accordingly,
     * and check if there's user-clicked detail to scroll into view.
    */
    this.forceUpdate(this.scrollDetailIntoView);
  }

  scrollDetailIntoView = () => {
    /* If there's a user-clicked detail specified in the state
     * and the container state is not minified
     * scroll the detail into view.
    */
    if (!this.minified) {
      const { detailToShow } = this.state;
      if (detailToShow) {
        // Set the detail in state to null once it has been revealed
        this.setState({ detailToShow: null }, () => {
          if ('scrollBehavior' in document.documentElement.style) {
            // Smooth scrolling supported
            detailToShow.scrollIntoView({ 'behavior': 'smooth' });
          } else {
            detailToShow.scrollIntoView();
          }
        });
      }
    }
  }

  showDetail = evnt => {
    /* User clicked a detail, set the state and toggle the container (Box)
     * minified-state. The callback from the container then scrolls the
     * detail into view after the container is un-minified.
    */
    if (this.minified) {
      // Trigger this only if the container in currently minifed.
      const detailToShow = evnt.currentTarget;
      this.setState({ detailToShow }, () => {
        this.boxRef.current.switchState();
      })
    }
  };

  get minified() {
    /* Helper function to check the parent container's (Box) minified-state.
    */
    return (
      this.boxRef.current
        ? this.boxRef.current.state.minified
        : true
    );
  }

  render() {
    const { intl, locale, report } = this.props;

    return (
      <Box
        headerLabel={<FormattedMessage { ...messages.title } />}
        onStateChange={this.onStateChange}
        ref={this.boxRef}
      >
        <Details minified={this.minified}>
          {['personnel', 'age_in_days', 'turnover'].map(detail => (
            <Detail key={`detail-${detail}`} minified={this.minified}>
              <DetailIcon
                onClick={this.showDetail}
                id={`report-details-${detail}`}
              >
                <CircleWidget
                  label={intl.formatMessage(messages[detail])}
                  circleType={detail}
                  circleData={report[detail]}
                  locale={locale}
                />
              </DetailIcon>

              <Minifiable>
                <FormattedHTMLMessage
                  {...messages[`${detail}_info`]}
                />
              </Minifiable>
            </Detail>
          ))}

          {['vat', 'employer', 'tax_prepayment'].map(detail => (
            <Detail key={`detail-${detail}`} minified={this.minified}>
              <DetailIcon
                onClick={this.showDetail}
                id={`report-details-${detail}`}
              >
                <CircleWidget
                  circleType={detail}
                  circleData={report.registrations}
                  locale={locale}
                />
              </DetailIcon>

              <Minifiable>
                <FormattedHTMLMessage
                  {...messages[
                    `${detail}_${report.registrations[detail]}_info`
                  ]}
                />
              </Minifiable>
            </Detail>
          ))}

          {['accident', 'liability'].map(detail => (
            <Detail key={`detail-${detail}`} minified={this.minified}>
              <DetailIcon
                onClick={this.showDetail}
                id={`report-details-${detail}`}
              >
                <CircleWidget
                  circleType={detail}
                  circleData={report.insurances}
                  locale={locale}
                />
              </DetailIcon>

              <Minifiable>
                <FormattedHTMLMessage
                  {...messages[
                    `${detail}_${report.insurances[detail]}_info`
                  ]}
                />
              </Minifiable>
            </Detail>
          ))}
        </Details>
      </Box>
    );
  }
}

ReportDetails.propTypes = propTypes;
ReportDetails.defaultProps = {
  editMode: false,
};

const Detail = styled.div`
  border-bottom: ${
    props => props.minified
      ? 'none'
      : `1px solid ${props.theme['grey-normal']}`
    };
  display: ${props => props.minified ? 'block' : 'flex'};
  flex: 1;
  flex-direction: column;
  margin-bottom: 24px;
  min-width: 210px;
  padding-bottom: ${props => props.minified ? '0px': '24px'};
  width: ${props => props.minified ? 'auto': '100%'};

  &:last-child {
    border-bottom: none;
  }

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const DetailIcon = styled.div`
  min-width: ${props => props.minified ? '0': '280px'};;
  width: ${props => props.minified ? 'auto': '100%'};

  @media (min-width: 640px) {
    width: ${props => props.minified ? 'auto': '280px'};
  }
`;

const Details = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: ${props => props.minified ? 'row' : 'column'};
  flex-wrap: wrap;
  margin-bottom: ${props => props.minified ? '-24px': '-48px'};
`;

export default injectIntl(ReportDetails);
